import { createSelector } from "@reduxjs/toolkit";

import { Permission } from "apis/dataV2/permissions";
import { type RootState } from "app";
import { defaultEnabledWidgets, featuresConfig, Widget } from "config/features";
import { FeatureToggle } from "config/featureToggles";
import { checkPermission } from "utils/auth";

import { ProjectType, SceneType, UrlSearchQuery, UxFlavor } from "./types";
import { getPositionedWidgets, getTakenWidgetSlotCount } from "./utils";

export const selectWidgets = (state: RootState) => state.explorer.widgets;
export const selectFavoriteWidgets = (state: RootState) => state.explorer.favoriteWidgets;
export const selectWidgetSlot = (state: RootState) => state.explorer.widgetSlot;
export const selectWidgetLayout = (state: RootState) => state.explorer.widgetLayout;
export const selectWidgetGroupPanelState = (state: RootState) => state.explorer.widgetGroupPanelState;
export const selectLockedWidgets = (state: RootState) => state.explorer.lockedWidgets;
export const selectLocalBookmarkId = (state: RootState) => state.explorer.localBookmarkId;
export const selectUrlBookmarkId = (state: RootState) => state.explorer.urlBookmarkId;
export const selectUrlSearchQuery = (state: RootState) => state.explorer.urlSearchQuery as UrlSearchQuery | undefined;
export const selectSceneType = (state: RootState) => state.explorer.sceneType;
export const selectRequireConsent = (state: RootState) => state.explorer.requireConsent;
export const selectOrganization = (state: RootState) => state.explorer.organization;
export const selectMaximized = (state: RootState) => state.explorer.maximized;
export const selectMaximizedHorizontal = (state: RootState) => state.explorer.maximizedHorizontal;
export const selectMinimized = (state: RootState) => state.explorer.minimized;
export const selectPrimaryMenu = (state: RootState) => state.explorer.primaryMenu;
export const selectIsAdminScene = (state: RootState) => state.explorer.sceneType === SceneType.Admin;
export const selectCanvasContextMenuFeatures = (state: RootState) => state.explorer.contextMenu.canvas.features;
export const selectIsOnline = (state: RootState) => state.explorer.isOnline;
export const selectConfig = (state: RootState) => state.explorer.config;
export const selectProcoreCompanyId = (state: RootState) => state.explorer.procoreContext?.companyId;
export const selectProcoreProjectId = (state: RootState) => state.explorer.procoreContext?.projectId;
export const selectProjectIsV2 = (state: RootState) => state.explorer.projectType === ProjectType.V2;
export const selectProjectV2Info = (state: RootState) => state.explorer.projectV2Info;
export const selectPipelineConfig = (state: RootState) => state.explorer.pipelineConfig;
export const selectCurrentProjectVersion = (state: RootState) => state.explorer.currentProjectVersion;
export const selectNewProjectVersionStatus = (state: RootState) => state.explorer.newProjectVersionStatus;
export const selectSceneDbBackfill = (state: RootState) => state.explorer.sceneDbBackfill;
export const selectTmZoneForCalc = (state: RootState) => state.explorer.tmZoneForCalc;
export const selectUxFlavor = (state: RootState) => state.explorer.uxFlavor;
export const selectSnackbarMessage = (state: RootState) => state.explorer.snackbarMessage;
export const selectHighlightSetting = (state: RootState) => state.explorer.highlightSetting;
export const selectGlobalSearchOpen = (state: RootState) => state.explorer.globalSearch.open;
export const selectIsVersionComparison = (state: RootState) => state.explorer.isVersionComparison;
export const selectUnitSystem = (state: RootState) => state.explorer.unitSystem;
export const selectSelectionResolution = (state: RootState) => state.explorer.selectionResolution;
export const selectNodeClassSelectionEnabled = (state: RootState) => state.explorer.nodeClassSelection;
export const selectCameraIsTooFar = (state: RootState) => state.explorer.cameraIsTooFar;
export const selectShowUnusuallyFarObjects = (state: RootState) => state.explorer.showUnusuallyFarObjects;
export const selectSceneId = (state: RootState) => state.explorer.sceneId;
/**
 * Dev-only escape hatch: `?forceMobile` enables the Procore mobile HUD in `npm run dev`
 * regardless of UX flavor, so the mobile header can be inspected without a Procore backend.
 * `import.meta.env.DEV` is statically `false` in production builds, so this whole expression
 * is tree-shaken away and has no effect on production behavior.
 */
export const isDevForceMobile =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("forceMobile");
export const selectIsProcoreDesign = (state: RootState) =>
    isDevForceMobile ||
    state.explorer.uxFlavor === UxFlavor.Procore ||
    state.explorer.uxFlavor === UxFlavor.ProcoreAndroid ||
    state.explorer.uxFlavor === UxFlavor.ProcoreIOS;
export const selectIsProcoreAndroid = (state: RootState) => state.explorer.uxFlavor === UxFlavor.ProcoreAndroid;
export const selectIsProcoreIOS = (state: RootState) => state.explorer.uxFlavor === UxFlavor.ProcoreIOS;
export const selectIsProcoreMobileDesign = (state: RootState) =>
    isDevForceMobile ||
    (new URLSearchParams(window.location.search).has("forceMobile") && state.explorer.uxFlavor === UxFlavor.Procore) ||
    state.explorer.uxFlavor === UxFlavor.ProcoreAndroid ||
    state.explorer.uxFlavor === UxFlavor.ProcoreIOS;
export const selectShowWeakDeviceMessage = (state: RootState) => state.explorer.showWeakDeviceMessage;
export const selectBackendProvider = (state: RootState) => state.explorer.backendProvider;
export const selectIsProcoreBackend = (state: RootState) => state.explorer.backendProvider === "procore";
export const selectProcoreContext = (state: RootState) => state.explorer.procoreContext;
export const selectIncrementalCachingDisabled = (state: RootState) =>
    (state.explorer.procoreContext?.disableIncrementalCaching ?? false) && selectIsProcoreIOS(state);
export const selectInitViewpointId = (state: RootState) => state.explorer.initViewpointId;
export const selectInitHomeViewpointId = (state: RootState) => state.explorer.initHomeViewpointId;
export const selectInitViewpoint = (state: RootState) => state.explorer.initViewpoint;
export const selectAppliedViewpoint = (state: RootState) => state.explorer.appliedViewpoint;
export const selectIsApplyingViewpoint = (state: RootState) => state.explorer.isApplyingViewpoint;

export const selectHotkeysEnabled = (state: RootState) => state.featureToggles[FeatureToggle.HotkeysEnabled];

export const selectNewDesign = createSelector([selectUxFlavor], (newDesign) => newDesign === UxFlavor.New);
export const selectProjectName = (state: RootState) => state.explorer.projectName;
export const selectAccessToken = (state: RootState) => state.auth.accessToken;

export const selectJoysticksMoving = (state: RootState) => state.explorer.joysticks.moving;

export const selectIsAxisSphereVisible = (state: RootState) => state.featureToggles[FeatureToggle.AxisSphereVisible];
export const selectOverlayElementsZIndex = (state: RootState) => state.explorer.uiConfig.overlayElementsZIndex;

export const selectIsMiniMapVisible = (state: RootState) => state.featureToggles[FeatureToggle.MiniMapVisible];
export const selectIsJoysticksVisible = (state: RootState) => state.featureToggles[FeatureToggle.JoysticksVisible];
export const selectIsSpaceMouseEnabled = (state: RootState) => state.featureToggles[FeatureToggle.SpaceMouseEnabled];
/** Dev-only, like `isDevForceMobile`: `?spaceMouse` stands in for the host flag in `npm run dev`. */
const isDevForceSpaceMouse =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("spaceMouse");
/** The host's rollout flag, as opposed to the user's setting (`selectIsSpaceMouseEnabled`). */
export const selectIsSpaceMouseAvailable = (state: RootState) => state.explorer.spaceMouse || isDevForceSpaceMouse;

/** Dev-only, like `isDevForceMobile`: `?objectTreeV2` stands in for the host flag in `npm run dev`. */
const isDevForceObjectTreeV2 =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("objectTreeV2");
export const selectObjectTreeV2 = (state: RootState) =>
    (state.explorer.procoreContext?.objectTreeV2 ?? false) || isDevForceObjectTreeV2;

/** Dev-only, like `isDevForceMobile`: `?gpuGuidance` stands in for the host flag in `npm run dev`. */
const isDevForceGpuGuidance =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("gpuGuidance");
export const selectGpuGuidanceEnabled = (state: RootState) =>
    ((state.explorer.procoreContext?.gpuGuidance ?? false) || isDevForceGpuGuidance) &&
    !selectIsProcoreMobileDesign(state);

/**
 * Local rollout toggle for the object-to-object measurement tool, kept in code until the
 * host starts passing a resolved rollout flag (for example, from LaunchDarkly) as
 * `InitOptions.procore.objectMeasure`. Flip this to expose the tool in every build; once the
 * host owns the rollout, delete it and rely on the flag alone.
 */
const OBJECT_MEASURE_ENABLED_LOCALLY = false;
/** Dev-only, like `isDevForceMobile`: `?objectMeasure` stands in for the host flag in `npm run dev`. */
const isDevForceObjectMeasure =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("objectMeasure");
export const selectObjectMeasureEnabled = (state: RootState) =>
    OBJECT_MEASURE_ENABLED_LOCALLY ||
    (state.explorer.procoreContext?.objectMeasure ?? false) ||
    isDevForceObjectMeasure;

/**
 * The host's rollout flag (BM-13597) for the server-tiled mini map drawing
 * route. When off, every drawing uses the legacy PDF route; when on, drawings
 * with an available tile set use tiles and the rest keep using PDF (see
 * `downloadDrawingData`). In local dev the lib-as-app demo passes it via
 * `InitOptions.procore.enableMinimapTiling` (index-lib-as-app.html).
 */
export const selectMinimapTilingEnabled = (state: RootState) =>
    state.explorer.procoreContext?.enableMinimapTiling ?? false;

export const selectEnabledWidgetsWithoutPermissionCheck = createSelector(
    (state: RootState) => state.explorer.enabledWidgets,
    (widgets) => widgets.map((widget) => featuresConfig[widget]).filter((config) => config) as Widget[],
);

export const selectEnabledWidgets = createSelector(
    [selectEnabledWidgetsWithoutPermissionCheck, selectProjectV2Info, selectNewDesign, selectIsVersionComparison],
    (widgets, projectInfo, newDesign, isVersionComparison) => {
        if (projectInfo) {
            const permissionSet = new Set(projectInfo.permissions);
            const can = (p: Permission) => checkPermission(permissionSet, p);

            return widgets.filter((w) => {
                if (defaultEnabledWidgets.includes(w.key)) {
                    return true;
                }

                if (isVersionComparison && !("versionComparison" in w && w.versionComparison)) {
                    return false;
                }

                // Temp permission for new UX components
                // TODO redo when new UX is released
                if ("newUx" in w && w.newUx) {
                    return newDesign;
                }

                switch (w.key) {
                    case "bimcollab":
                    case "bimTrack":
                    case "ditio":
                    case "jira":
                    case "arcgis":
                    case "omegaPims365":
                    case "xsiteManage":
                        return can(`int:${w.key}:use` as Permission);
                    case "omega365":
                        return can(`int:omegaPims365:use` as Permission);
                    case "bookmarks":
                        return can(Permission.BookmarkRead);
                    case "clash":
                        return can(Permission.ClashRead);
                    case "groups":
                        return can(Permission.GroupRead);
                    case "forms":
                        return can(Permission.FormsView);
                    case "deviations":
                        return can(Permission.DeviationRead);
                    case "advancedSettings":
                        return can(Permission.SceneManage);
                    default:
                        return can(`widget:${w.key}` as Permission);
                }
            });
        }

        return widgets;
    },
);

export const selectEnabledWidgetKeys = createSelector(
    [selectEnabledWidgets],
    (widgets) => new Set(widgets.map((widget) => widget.key)),
);

export const selectGridSize = createSelector([selectWidgetLayout], (layout) => ({
    width: layout.widgets === 4 ? 2 : 1,
    height: layout.widgets === 1 ? 1 : 2,
}));

export const selectPositionedWidgets = createSelector(
    [selectWidgets, selectGridSize, selectMaximized, selectMaximizedHorizontal, selectWidgetLayout],
    (widgets, { width, height }, maximized, maximizedHorizontal, layout) => {
        const gridHeight = layout.sideBySide ? height : 2;
        return getPositionedWidgets({ widgets, gridWidth: width, gridHeight, maximized, maximizedHorizontal });
    },
);

export const selectCanAddWidget = createSelector(
    [selectWidgetLayout, selectWidgets, selectMaximized, selectMaximizedHorizontal],
    (layout, widgets, maximized, maximizedHorizontal) =>
        getTakenWidgetSlotCount(widgets, maximized, maximizedHorizontal) < layout.widgets,
);
